import { Filter } from '@/models/filter.model';
import { Pagination } from '@/models/pagination.model';
import { ArrayCreateGenericViability, ArrayGetGenericViability } from '@/models/proposal-order.model';
import { GetRequestList, RequestSimulate, SendRequestDeleteTask, SendRequestInventory, SendRequestOptional, SendRequestResearch, SendRequestRestocking } from '@/models/request.model';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseApi } from './base/base.api';

@Injectable({
  providedIn: 'root',
})
export abstract class OrderRequestApi extends BaseApi {

  async getListProposalRequests(filter?: Filter): Promise<Pagination<GetRequestList>> {
    let params = new HttpParams();
    params = params.append("IsPagingEnabled", "true");
    if (filter) {
      Object.keys(filter).forEach(key => {
        if (filter[key as keyof Filter]) {
          params = params.append(key, filter[key as keyof Filter].toString());
        }
      });
    }

    const response = await this.get<{ succeeded: boolean, data: Pagination<GetRequestList> }>(`${this.url}/proposal-requests/list-all`, { params });
    return response.data;
  }

  async getListProposalRequestsFilter(filter?: any): Promise<Pagination<GetRequestList>> {
    let params = new HttpParams();
    params = params.append("IsPagingEnabled", "true");
    if (filter) {
      Object.keys(filter).forEach((key) => {
        const value = filter[key as keyof Filter];
        if (value !== null && value !== undefined) {
          params = params.append(key, value.toString());
        }
      });
    }

    const response = await this.get<{ succeeded: boolean, data: Pagination<GetRequestList> }>(`${this.url}/proposal-requests/list-all`, { params });
    return response.data;
  }

  async getListProposalRequestsSearch(search?: any): Promise<Pagination<GetRequestList>> {
    let params = new HttpParams();
    params = params.append("IsPagingEnabled", "true");
    params = params.append("search", `${search}`);

    const response = await this.get<{ succeeded: boolean, data: Pagination<GetRequestList> }>(`${this.url}/proposal-requests/list-all`, { params });
    return response.data;
  }

  async calculateProposalRequest(payload: any): Promise<any> {
    return this.post(`${this.url}/proposal-requests/calculate`, payload);
  }

  async getProposalRequest(proposalId: string): Promise<RequestSimulate> {

    const response = await this.post<{ succeeded: boolean, data: RequestSimulate }>(`${this.url}/proposal-requests/simulate/${proposalId}`, {});
    return response.data;
  }

  async getProposalRequestById(proposalRequestId: string): Promise<RequestSimulate> {

    const response = await this.get<{ succeeded: boolean, data: RequestSimulate }>(`${this.url}/proposal-requests/${proposalRequestId}`);
    return response.data;
  }

  addRestockingStore(proposalRequestId: string, stores: SendRequestRestocking[]): Promise<RequestSimulate> {
    return this.post(`${this.url}/proposal-requests/${proposalRequestId}/restocking-stores`, stores);
  }

  addResearchStore(proposalRequestId: string, stores: SendRequestResearch[]): Promise<RequestSimulate> {
    return this.post(`${this.url}/proposal-requests/${proposalRequestId}/research-stores`, stores);
  }

  addInventoryStore(proposalRequestId: string, stores: SendRequestInventory[]): Promise<RequestSimulate> {
    return this.post(`${this.url}/proposal-requests/${proposalRequestId}/inventory-stores`, stores);
  }

  updateRestockingStore(proposalRequestId: string, stores: SendRequestRestocking): Promise<RequestSimulate> {
    return this.put(`${this.url}/proposal-requests/${proposalRequestId}/restocking-stores`, [stores]);
  }
  updateMassiveRestockingStore(proposalRequestId: string, stores: SendRequestRestocking[]): Promise<RequestSimulate> {
    return this.put(`${this.url}/proposal-requests/${proposalRequestId}/restocking-stores`, stores);
  }
  updateMassiveResearchStore(proposalRequestId: string, stores: SendRequestResearch[]): Promise<RequestSimulate> {
    return this.put(`${this.url}/proposal-requests/${proposalRequestId}/research-stores`, stores);
  }
  updateMassiveInventoryStore(proposalRequestId: string, stores: SendRequestInventory[]): Promise<RequestSimulate> {
    return this.put(`${this.url}/proposal-requests/${proposalRequestId}/inventory-stores`, stores);
  }

  updateMassiveOptionalStore(proposalRequestId: string, stores: SendRequestOptional[]): Promise<RequestSimulate> {
    return this.put(`${this.url}/proposal-requests/${proposalRequestId}/optional-costs`, stores);
  }

  updateResearchStore(proposalRequestId: string, stores: SendRequestResearch): Promise<RequestSimulate> {
    return this.put(`${this.url}/proposal-requests/${proposalRequestId}/research-stores`, [stores]);
  }

  updateInventoryStore(proposalRequestId: string, stores: SendRequestInventory): Promise<RequestSimulate> {
    return this.put(`${this.url}/proposal-requests/${proposalRequestId}/inventory-stores`, [stores]);
  }

  updateOptionalStore(proposalRequestId: string, stores: SendRequestOptional): Promise<RequestSimulate> {
    return this.put(`${this.url}/proposal-requests/${proposalRequestId}/optional-costs`, [stores]);
  }

  deleteRestockingRequestStore(proposalRequestId: string, stores: SendRequestDeleteTask[]): Promise<RequestSimulate> {
    return this.delete(`${this.url}/proposal-requests/${proposalRequestId}/restocking-stores`, stores);
  }
  deleteResearchRequestStore(proposalRequestId: string, stores: SendRequestDeleteTask[]): Promise<RequestSimulate> {
    return this.delete(`${this.url}/proposal-requests/${proposalRequestId}/research-stores`, stores);
  }
  deleteInventoryRequestStore(proposalRequestId: string, stores: SendRequestDeleteTask[]): Promise<RequestSimulate> {
    return this.delete(`${this.url}/proposal-requests/${proposalRequestId}/inventory-stores`, stores);
  }

  deleteOptionalRequest(proposalRequestId: string, stores: string[]): Promise<RequestSimulate> {
    return this.delete(`${this.url}/proposal-requests/${proposalRequestId}/optional-costs`, stores);
  }

  deleteProposalRequestCard(proposalRequestId: string): Promise<void> {
    return this.delete(`${this.url}/proposal-requests/${proposalRequestId}`)
  }

  concludeRequest(proposalRequestId: string): Promise<boolean> {
    return this.post(`${this.url}/proposal-requests/${proposalRequestId}/next-step`, {});
  }

  async generateProposalRequestPdf(id: string): Promise<Blob> {
    const response = await this.http
      .post(`${this.url}/proposal-requests/${id}/pdf`, {}, { responseType: 'blob' })
      .toPromise();
    return response as Blob;
  } 

  async generateProposalRequestExcel(id: string): Promise<Blob> {
    const response = await this.http
      .post(`${this.url}/proposal-requests/${id}/excel`, null, {
        responseType: 'blob'
      })
      .toPromise();
    return response as Blob;
  } 

  async getViability(proposalId: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposal-requests/${proposalId}/viability`));

    return response.data
  }

  async getViabilityBySearch(proposalId: string, text: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposal-requests/${proposalId}/viability?search=${text}`));

    return response.data
  }

  async getViabilityStartOperation(proposalId: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposal-requests/${proposalId}/viability?viability=true`));

    return response.data
  }
  async getViabilityStartOperationBySearch(proposalId: string, text: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposal-requests/${proposalId}/viability?viability=true&search=${text}`));

    return response.data
  }

  async getViabilitiesByFlag(flagId: string, proposalId: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposal-requests/${proposalId}/viability?BrandId=${flagId}`));

    return response.data
  }
  async getViabilitiesByState(state: string, proposalId: string): Promise<ArrayGetGenericViability> {
    const response = await firstValueFrom(this.http.get<{ succeeded: boolean, data: ArrayGetGenericViability }>(
      `${this.url}/proposal-requests/${proposalId}/viability?State=${state}`));

    return response.data
  }

  async createViability(proposalId: string, payload: ArrayCreateGenericViability): Promise<any> {
    return this.post(`${this.url}/proposal-requests/${proposalId}/create-viability`, payload);
  }

  async updateViability(proposalId: string, payload: ArrayCreateGenericViability): Promise<any> {
    return this.put(`${this.url}/proposal-requests/${proposalId}/update-viability`, payload);
  }
}
